/**
 * NDE Technician Register. CR-2026-NDE-REG (2026-10-15). Server: /api/welding/inspector-qualifications.
 * The certificates Gate 5 and the Quality Data Package read to decide whether a technician may sign an NDE result on its
 * inspection date. A certificate is never edited once recorded (TR-NDE-QUAL-001): a wrong entry is revoked with a reason
 * and recorded again. Status moves forward only: mark expired (counts only for inspections before the day it is marked)
 * or revoke (never counts). Adding and changing need manage_welder_qualifications (Admin, Quality Manager); the server
 * checks it. CR-2026-EXT-NDT: a certificate names a registered technician: a company user, or an outside lab's technician
 * (name and employer, no SFM login), recorded by the QM. A registered technician is never edited (TR-NDE-TECH-001).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import {
  Alert, Button, Card, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import ApiCall from "../../utils/apicall";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import { messageOf } from "./WeldRecords";

import SmartTable from "../../components/SmartTable/SmartTable";
/** Certification types the register accepts (models/NdeInspectorQualification.js CERT_TYPES, as deployed 2026-10-15). */
export const CERT_TYPES = ["CWI", "SCWI", "CWE", "AWS-CAWI", "MT-LII", "PT-LII", "UT-LII", "UT-LIII", "RT-LII", "OTHER"];
const day = (d) => (d ? String(d).slice(0, 10) : "");
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Why a new certificate cannot be saved yet, or "" when it can. The server checks the same and more. */
export function certProblem(f) {
  if (!f.inspector_id) return "Choose the technician.";
  if (!CERT_TYPES.includes(f.cert_type)) return "Choose the certification type.";
  if (f.issued_date && !DAY.test(f.issued_date)) return "The issue date is not a date.";
  if (f.expiry_date && !DAY.test(f.expiry_date)) return "The expiry date is not a date.";
  if (f.issued_date && f.expiry_date && f.issued_date > f.expiry_date) return "The issue date is after the expiry date.";
  return "";
}
/** Why a technician cannot be registered yet, or "" when they can. */
export function techProblem(t) {
  if (t.kind === "company") return t.user_id ? "" : "Choose the user.";
  if (t.kind === "external") {
    if (String(t.full_name || "").trim().length < 3) return "Give the technician's full name.";
    if (String(t.employer || "").trim().length < 2) return "Give the employer (the NDT lab).";
    return "";
  }
  return "Choose company user or outside lab.";
}
/** Technician as shown in lists: name, and the employer for an outside lab's technician. */
export const techLabel = (t) => (t ? `${t.full_name}${t.kind === "external" && t.employer ? ` (${t.employer})` : ""}` : "");
/** Which status changes the user may make on this certificate. */
export function statusActions(q, canManage) {
  if (!canManage) return { expire: false, revoke: false };
  return { expire: q.status === "active", revoke: q.status !== "revoked" };
}
/** The status as one line: since when and why, when it was changed. */
export function statusLine(q) {
  if (q.status === "active") return "Active";
  const when = day(q.status_changed_at || q.updatedAt || q.createdAt);
  const label = q.status === "expired" ? "Expired" : "Revoked";
  return `${label}${when ? ` since ${when}` : ""}${q.status_change_reason ? `: ${q.status_change_reason}` : ""}`;
}

const blank = { inspector_id: "", cert_type: "", cert_number: "", issued_date: "", expiry_date: "", issuing_body: "" };

export default function NdeTechnicianRegister() {
  const { loginUser } = useSelector((s) => s.userSlice);
  const canManage = useMemo(() => hasPermission(loginUser?.decodedToken?.permissions || [], PERMISSIONS.MANAGE_WELDER_QUALIFICATIONS), [loginUser]);
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [techs, setTechs] = useState([]);
  const [tform, setTform] = useState({ kind: "company", user_id: "", full_name: "", employer: "" });
  const [form, setForm] = useState(blank);
  const [dlg, setDlg] = useState(null);   // { q, status, reason }
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await ApiCall.get("/welding/inspector-qualifications");
      const list = Array.isArray(r.data) ? r.data : (Array.isArray(r.data?.data) ? r.data.data : []);
      setRows(list);
      const t = await ApiCall.get("/welding/nde-technicians");
      setTechs(Array.isArray(t.data?.data) ? t.data.data : []);
    } catch (e) { setMsg({ severity: "error", text: messageOf(e, "Could not load the register.") }); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!canManage) return;
    ApiCall.get("/user").then((r) => setUsers((r.data?.data?.users || []).filter((u) => u.isActive !== false && u.isActive !== 0))).catch(() => setUsers([]));
  }, [canManage]);

  const act = async (fn, ok) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg({ severity: "success", text: ok }); setDlg(null); await load(); return true; }
    catch (e) { setMsg({ severity: "error", text: messageOf(e, "Not saved.") }); return false; }
    finally { setBusy(false); }
  };
  const problem = certProblem(form);
  const tproblem = techProblem(tform);
  const techById = useMemo(() => Object.fromEntries(techs.map((t) => [String(t.id), t])), [techs]);
  const registeredUsers = useMemo(() => new Set(techs.filter((t) => t.kind === "company").map((t) => String(t.user_id))), [techs]);
  const register = () => act(() => ApiCall.post("/welding/nde-technicians", tform.kind === "company"
    ? { kind: "company", user_id: Number(tform.user_id) }
    : { kind: "external", full_name: tform.full_name.trim(), employer: tform.employer.trim() }), "Technician registered.")
    .then((ok) => ok && setTform({ kind: tform.kind, user_id: "", full_name: "", employer: "" }));
  const add = () => act(() => ApiCall.post("/welding/inspector-qualifications", {
    inspector_id: Number(form.inspector_id), cert_type: form.cert_type, cert_number: form.cert_number.trim() || null,
    issued_date: form.issued_date || null, expiry_date: form.expiry_date || null, issuing_body: form.issuing_body.trim() || null,
  }), "Certificate recorded.").then((ok) => ok && setForm(blank));
  const change = () => act(() => ApiCall.put(`/welding/inspector-qualifications/${dlg.q.id}/status`, { status: dlg.status, reason: dlg.reason.trim() }),
    dlg.status === "revoked" ? "Certificate revoked. It no longer counts for any inspection." : "Certificate marked expired. It counts only for inspections dated before the day it was marked (UTC date).");

  return (
    <>
      <Helmet><title>NDE technician register | SFM</title></Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 1 }}>NDE technician register</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The certificates SFM checks before a technician can sign an NDE result, judged on the inspection date. A certificate is not
          edited once recorded: revoke a wrong entry with a reason and record it again. Mark expired when a certification stops early
          (suspension, lapsed eye test): it then counts only for inspections dated before that day. Revoked never counts.
        </Typography>
        {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
        {canManage && (
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Register a technician</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              A company user, or an outside lab&apos;s technician (subcontracted UT, RT, MT, PT) with their employer. Outside technicians get no SFM login.
              A registered technician is not edited; register again if an entry is wrong.
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={2}><TextField select fullWidth size="small" label="Who" value={tform.kind} onChange={(e) => setTform({ kind: e.target.value, user_id: "", full_name: "", employer: "" })}>
                <MenuItem value="company">Company user</MenuItem><MenuItem value="external">Outside lab technician</MenuItem></TextField></Grid>
              {tform.kind === "company" ? (
                <Grid item xs={12} sm={6} md={4}><TextField select fullWidth size="small" label="User" value={tform.user_id} onChange={(e) => setTform({ ...tform, user_id: e.target.value })}>
                  {users.filter((u) => !registeredUsers.has(String(u.id))).map((u) => <MenuItem key={u.id} value={String(u.id)}>{[u.firstName, u.lastName].filter(Boolean).join(" ") || u.email}</MenuItem>)}</TextField></Grid>
              ) : (<>
                <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" label="Full name" value={tform.full_name} onChange={(e) => setTform({ ...tform, full_name: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" label="Employer (NDT lab)" value={tform.employer} onChange={(e) => setTform({ ...tform, employer: e.target.value })} /></Grid>
              </>)}
              <Grid item xs={12} sm={6} md={2}><Button variant="contained" disabled={busy || !!tproblem} onClick={register}>Register</Button></Grid>
            </Grid>
          </Card>
        )}
        {canManage ? (
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Record a certificate</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}><TextField select fullWidth size="small" label="Technician (registered)" value={form.inspector_id} onChange={(e) => setForm({ ...form, inspector_id: e.target.value })}>
                {techs.map((t) => <MenuItem key={t.id} value={String(t.id)}>{techLabel(t)}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} sm={6} md={1.5}><TextField select fullWidth size="small" label="Type" value={form.cert_type} onChange={(e) => setForm({ ...form, cert_type: e.target.value })}>
                {CERT_TYPES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} sm={6} md={1.5}><TextField fullWidth size="small" label="Number" value={form.cert_number} onChange={(e) => setForm({ ...form, cert_number: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6} md={1.5}><TextField fullWidth size="small" type="date" label="Issued" InputLabelProps={{ shrink: true }} value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6} md={1.5}><TextField fullWidth size="small" type="date" label="Expires" InputLabelProps={{ shrink: true }} value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" label="Issuing body (AWS, ASNT SNT-TC-1A employer, ...)" value={form.issuing_body} onChange={(e) => setForm({ ...form, issuing_body: e.target.value })} /></Grid>
            </Grid>
            {problem && form.inspector_id && <Typography variant="caption" color="error">{problem}</Typography>}
            <div><Button sx={{ mt: 1.5 }} variant="contained" disabled={busy || !!problem} onClick={add}>Record certificate</Button></div>
          </Card>
        ) : <Alert severity="info" sx={{ mb: 2 }}>Recording and changing certificates needs the Manage welder qualifications permission (Admin, Quality Manager).</Alert>}
        <Card sx={{ overflowX: "auto" }}>
          {rows.length === 0 ? <Typography sx={{ p: 2 }}>No certificates recorded.</Typography> : (
            <SmartTable tableId="nde-technicians" lockedColumns={["Technician", "Expires", "Status"]} size="small">
              <TableHead><TableRow>{["Technician", "Type", "Number", "Issued", "Expires", "Issuing body", "Status", ""].map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>{rows.map((q) => {
                const a = statusActions(q, canManage);
                return (
                  <TableRow key={q.id}>
                    <TableCell>{techById[String(q.inspector_id)] ? techLabel(techById[String(q.inspector_id)]) : q.inspector_name}</TableCell><TableCell>{q.cert_type}</TableCell><TableCell>{q.cert_number || ""}</TableCell>
                    <TableCell>{day(q.issued_date)}</TableCell><TableCell>{day(q.expiry_date)}</TableCell><TableCell>{q.issuing_body || ""}</TableCell>
                    <TableCell><Chip size="small" label={q.status} color={q.status === "active" ? "success" : q.status === "expired" ? "warning" : "default"} sx={{ mr: 1 }} />
                      {q.status !== "active" && <Typography variant="caption">{statusLine(q)}</Typography>}</TableCell>
                    <TableCell>
                      {a.expire && <Button size="small" onClick={() => setDlg({ q, status: "expired", reason: "" })}>Mark expired</Button>}
                      {a.revoke && <Button size="small" color="error" onClick={() => setDlg({ q, status: "revoked", reason: "" })}>Revoke</Button>}
                    </TableCell>
                  </TableRow>
                );
              })}</TableBody>
            </SmartTable>
          )}
        </Card>
        <Dialog open={!!dlg} onClose={() => setDlg(null)} fullWidth maxWidth="sm">
          {dlg && <>
            <DialogTitle>{dlg.status === "revoked" ? "Revoke" : "Mark expired"}: {dlg.q.inspector_name} {dlg.q.cert_type} {dlg.q.cert_number || ""}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {dlg.status === "revoked" ? "A revoked certificate never counts, for any inspection date. This cannot be undone." :
                  "From now this certificate counts only for inspections dated before the day it is marked (the UTC date, so after 8 pm Eastern that is tomorrow's date). This cannot be undone; a recertified technician gets a new certificate."}
              </Typography>
              <TextField autoFocus fullWidth multiline minRows={2} label="Reason (required)" value={dlg.reason} onChange={(e) => setDlg({ ...dlg, reason: e.target.value })} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDlg(null)}>Cancel</Button>
              <Button variant="contained" color={dlg.status === "revoked" ? "error" : "warning"} disabled={busy || dlg.reason.trim().length < 10} onClick={change}>Save</Button>
            </DialogActions>
          </>}
        </Dialog>
      </Container>
    </>
  );
}
